import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, authHeaders } from "@/lib/api";
import { Upload, Trash2, ImageIcon } from "lucide-react";

const MAX_BYTES = 4 * 1024 * 1024; // 4 MB

export default function MoodboardUploader() {
  const [url, setUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const fetchCurrent = async () => {
    try {
      const res = await api.get("/moodboard");
      setUrl(res.data?.url || null);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchCurrent();
  }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image must be under 4 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setSaving(true);
      try {
        await api.post("/moodboard", { data_url: dataUrl }, { headers: authHeaders() });
        setUrl(dataUrl);
        toast.success("Moodboard updated. Refresh the landing page to see it live.");
      } catch {
        toast.error("Upload failed.");
      } finally {
        setSaving(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await api.delete("/moodboard", { headers: authHeaders() });
      setUrl(null);
      toast.success("Moodboard cleared.");
    } catch {
      toast.error("Failed to clear.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="border p-6 sm:p-8 mb-12"
      style={{ background: "rgba(48, 23, 40,0.55)", borderColor: "rgba(221, 191, 207,0.2)" }}
      data-testid="moodboard-uploader"
    >
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="small-caps mb-2" style={{ color: "#B9929D" }}>
            Evening moodboard
          </div>
          <h2 className="font-display" style={{ fontSize: "32px", color: "#F2EEE7", lineHeight: 1.1 }}>
            The <em style={{ color: "#DDBFCF" }}>image</em> guests see
          </h2>
        </div>
        <span className="text-xs italic" style={{ color: "#DDBFCF", opacity: 0.6 }}>
          Portrait works best. Under 4 MB.
        </span>
      </div>

      <div className="flex flex-col sm:flex-row gap-6">
        <div
          className="overflow-hidden flex items-center justify-center"
          style={{
            width: "180px",
            aspectRatio: "3 / 4",
            border: "1px solid rgba(221, 191, 207,0.25)",
            background: "rgba(24, 10, 19,0.5)",
            borderRadius: "4px",
            flexShrink: 0,
          }}
        >
          {url ? (
            <img
              src={url}
              alt="Current moodboard"
              className="w-full h-full object-cover"
              data-testid="current-moodboard-img"
            />
          ) : (
            <ImageIcon size={28} style={{ color: "#DDBFCF", opacity: 0.4 }} />
          )}
        </div>

        <div className="flex-1 flex flex-col justify-center gap-4">
          <p style={{ color: "#F2EEE7", opacity: 0.8, lineHeight: 1.6 }}>
            {url
              ? "This image is currently displayed on the public landing page."
              : "No image uploaded yet — the landing shows a placeholder."}
          </p>
          <div className="flex flex-wrap gap-3">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              data-testid="moodboard-file-input"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={saving}
              data-testid="moodboard-upload-btn"
              className="btn-primary"
            >
              <Upload size={14} /> {saving ? "Saving…" : url ? "Replace image" : "Upload image"}
            </button>
            {url && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={saving}
                data-testid="moodboard-remove-btn"
                className="btn-ghost"
              >
                <Trash2 size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
